// Per-entry & all-entries "proof of presence" exports (CSV + branded landscape PDF)
// for the Person Log Book (Drivers / Staff / Passengers). Reuses the exact same
// scaffolding as the Vehicle Driver Log Book so both stay visually consistent.
import { formatDuration, formatDistance, getFileTimestamp } from './timezone';
import {
  newLandscapeDoc,
  addBrandHeader,
  addFooters,
  loadLogoPng,
  downloadBlob,
  rowsToCsv,
  safeName,
  roleLabel,
  fmtCoords,
  tripLocMapsUrl,
  csvMapsLink,
  SLATE_900,
  SLATE_600,
  SLATE_500,
  BLUE,
  GREEN,
  BORDER,
  assignmentAuditRows,
  formatExportDateTime,
} from './tripExport';

const stamp = () => getFileTimestamp();

const entryStatusLabel = (e) => {
  if (e.event_type === 'Trip') return e.is_active ? 'Ongoing trip' : 'Trip';
  if (e.event_type === 'observed') return 'BLE observation';
  if (e.event_type === 'Mid-trip Driver Scan Ignored') return 'Driver scan ignored';
  return e.event_type || 'Entry';
};

// ----------------------------- CSV -----------------------------

const entryCsvRows = (e, idx) => {
  const rows = [];
  rows.push([`Entry #${idx + 1}`, entryStatusLabel(e)]);
  if (e.event_type === 'Trip') {
    rows.push(['Vehicle', e.vehicle_name || '-']);
    rows.push(['Role', roleLabel(e.role || e.person_type)]);
    rows.push(['Boarded', formatExportDateTime(e.started_at)]);
    rows.push(['Left', e.is_active ? 'Still onboard' : formatExportDateTime(e.ended_at)]);
    rows.push(['Duration', e.duration_seconds != null ? formatDuration(e.duration_seconds) : '-']);
    rows.push(['Distance', e.distance_km != null ? formatDistance(e.distance_km, { decimals: 1 }) : '-']);
    rows.push(['Source', e.source_label || '-']);
    rows.push(['Credential', e.credential_identifier || '-']);
    rows.push(['Start location', csvMapsLink(e.start_location)]);
    rows.push(['End location', csvMapsLink(e.end_location)]);
    if (e.allocation_note) rows.push(['Allocation / decision', e.allocation_note]);
    rows.push(...assignmentAuditRows(e.assignment_audit));
  } else if (e.event_type === 'observed') {
    rows.push(['Time', formatExportDateTime(e.timestamp)]);
    rows.push(['Summary', e.summary || '-']);
    rows.push(['Source', e.source_label || '-']);
    rows.push(['Confidence', e.confidence || '-']);
    rows.push(['RSSI', e.rssi_dbm != null ? `${e.rssi_dbm} dBm` : '-']);
    rows.push(['Battery', e.battery_voltage_mv != null ? `${e.battery_voltage_mv} mV` : '-']);
    rows.push(['Location', csvMapsLink(e.start_location)]);
  } else {
    rows.push(['Time', formatExportDateTime(e.timestamp)]);
    rows.push(['Vehicle', e.vehicle_name || '-']);
    rows.push(['Summary', e.summary || '-']);
    rows.push(['Source', e.source_label || '-']);
    rows.push(['Credential', e.credential_identifier || '-']);
    rows.push(['Location', csvMapsLink(e.start_location)]);
  }
  return rows;
};

export const exportEntryCsv = (entry, personName, idx = 0) => {
  const rows = [['Person', personName || ''], []].concat(entryCsvRows(entry, idx));
  downloadBlob(rowsToCsv(rows), `${safeName(personName)}-logbook-entry-${idx + 1}-${stamp()}.csv`, 'text/csv;charset=utf-8;');
};

export const exportAllEntriesCsv = (entries, personName) => {
  let rows = [['Person', personName || ''], ['Entries', String((entries || []).length)], ['Exported', formatExportDateTime(new Date().toISOString())], []];
  (entries || []).forEach((e, idx) => { rows = rows.concat(entryCsvRows(e, idx), [[], []]); });
  downloadBlob(rowsToCsv(rows), `${safeName(personName)}-logbook_${stamp()}.csv`, 'text/csv;charset=utf-8;');
};

// ----------------------------- PDF -----------------------------

const drawEntryBlock = (doc, layout, e, idx, startY) => {
  const { M, CONTENT_W, PAGE_H } = layout;
  let y = startY;

  doc.setFontSize(11); doc.setFont(undefined, 'bold'); doc.setTextColor(...SLATE_900);
  let title;
  if (e.event_type === 'Trip') {
    const end = e.is_active ? 'Ongoing' : formatExportDateTime(e.ended_at);
    title = `${e.vehicle_name || 'Vehicle'}   ${formatExportDateTime(e.started_at)}  ->  ${end}`;
  } else {
    title = `${entryStatusLabel(e)}   ${formatExportDateTime(e.timestamp)}`;
  }
  doc.text(doc.splitTextToSize(title, CONTENT_W - 160)[0] || title, M, y);

  doc.setFontSize(9); doc.setFont(undefined, 'bold');
  doc.setTextColor(...(e.is_active ? GREEN : SLATE_500));
  if (e.event_type === 'Trip') {
    const durDist = [
      e.duration_seconds != null ? formatDuration(e.duration_seconds) : null,
      e.distance_km != null ? formatDistance(e.distance_km, { decimals: 1 }) : null,
    ].filter(Boolean).join('  |  ');
    if (durDist) doc.text(durDist, layout.RIGHT, y, { align: 'right' });
  }
  y += 18;

  if (e.event_type === 'Trip' && e.summary) {
    doc.setFontSize(9); doc.setFont(undefined, 'normal'); doc.setTextColor(...SLATE_600);
    doc.text(doc.splitTextToSize(e.summary, CONTENT_W)[0] || e.summary, M, y); y += 14;
  }

  // GPS location links (clickable -> Google Maps)
  const startUrl = tripLocMapsUrl(e.start_location);
  const endUrl = tripLocMapsUrl(e.end_location);
  if (startUrl || endUrl) {
    doc.setFontSize(8); doc.setFont(undefined, 'normal'); doc.setTextColor(...BLUE);
    let lx = M;
    const sLabel = e.event_type === 'Trip' ? 'Start' : 'Location';
    if (startUrl) { const t = `${sLabel}: ${fmtCoords(e.start_location)}`; doc.textWithLink(t, lx, y, { url: startUrl }); lx += doc.getTextWidth(t) + 24; }
    if (endUrl) { const t = `End: ${fmtCoords(e.end_location)}`; doc.textWithLink(t, lx, y, { url: endUrl }); }
    y += 16;
  }

  // Key/value details
  const details = [];
  if (e.event_type === 'Trip') {
    details.push(['Role', roleLabel(e.role || e.person_type)]);
    details.push(['Source', e.source_label || '-']);
    details.push(['Credential', e.credential_identifier || '-']);
    if (e.allocation_note) details.push(['Allocation / decision', e.allocation_note]);
    details.push(...assignmentAuditRows(e.assignment_audit));
  } else if (e.event_type === 'observed') {
    details.push(['Source', e.source_label || '-']);
    details.push(['Confidence', e.confidence || '-']);
    details.push(['RSSI', e.rssi_dbm != null ? `${e.rssi_dbm} dBm` : '-']);
    details.push(['Battery', e.battery_voltage_mv != null ? `${e.battery_voltage_mv} mV` : '-']);
  } else {
    details.push(['Vehicle', e.vehicle_name || '-']);
    details.push(['Source', e.source_label || '-']);
    details.push(['Credential', e.credential_identifier || '-']);
  }
  doc.setFontSize(8);
  details.forEach(([k, v]) => {
    const lines = doc.splitTextToSize(String(v), CONTENT_W - 90);
    if (y + Math.max(14, lines.length * 11) > PAGE_H - 44) { doc.addPage(); y = 44; }
    doc.setFont(undefined, 'bold'); doc.setTextColor(...SLATE_600); doc.text(`${k}:`, M, y);
    doc.setFont(undefined, 'normal'); doc.setTextColor(...SLATE_900);
    doc.text(lines, M + 70, y);
    y += Math.max(13, lines.length * 11);
  });
  y += 6;
  return y;
};

export const exportEntryPdf = async (entry, personName, idx = 0) => {
  const { doc, layout } = newLandscapeDoc();
  const logo = await loadLogoPng();
  let y = addBrandHeader(doc, layout, logo, 'Person Log Book Entry', personName);
  y = drawEntryBlock(doc, layout, entry, idx, y);
  addFooters(doc, layout);
  doc.save(`${safeName(personName)}-logbook-entry-${idx + 1}-${stamp()}.pdf`);
};

export const buildAllEntriesPdf = async (entries, personName) => {
  const { doc, layout } = newLandscapeDoc();
  const logo = await loadLogoPng();
  let y = addBrandHeader(doc, layout, logo, 'Person Log Book', `${personName || '-'} \u00b7 ${(entries || []).length} entries`);
  const list = entries || [];
  list.forEach((e, i) => {
    if (y > layout.PAGE_H - 120) { doc.addPage(); y = 44; }
    y = drawEntryBlock(doc, layout, e, i, y);
    y += 12;
    if (i < list.length - 1) { doc.setDrawColor(...BORDER); doc.setLineWidth(0.5); doc.line(layout.M, y - 6, layout.RIGHT, y - 6); }
  });
  if (list.length === 0) { doc.setFontSize(10); doc.setTextColor(...SLATE_500); doc.text('No entries found for the selected filters.', layout.M, y); }
  addFooters(doc, layout);
  return { doc, filename: `${safeName(personName)}-logbook_${stamp()}.pdf` };
};

export const exportAllEntriesPdf = async (entries, personName) => {
  const { doc, filename } = await buildAllEntriesPdf(entries, personName);
  doc.save(filename);
};
